import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IApprovalRequest } from '../../definitions/Approval'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import useCommand from '../hooks/useCommand'
import Tile from '../layout/Tile'
import ApprovalUsersModal from './ApprovalsUsersModal'
import ApprovalTableUsers from './ApprovalTableUsers'

const ApprovalsUsers = () => {
    const [openModal, setOpenModal] = useState(false)
    const [selectedApproval, setSelectedApproval] = useState<IApprovalRequest>(null)
    const dispatch = useAppDispatch()

    const { execute: navigateToContract, status: navigateToContractStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => {
            const contract = await ApiClient.getContract(item.approvalEntityUUID)
            const contractFiles = await ApiClient.getContractFiles(item.approvalEntityUUID)

            return { contract, contractFiles }
        },
        onFinish: result => {
            dispatch(
                changeRoute({
                    route: 'contract-editor',
                    params: {
                        contractUUID: result.response.contract.uuid,
                        uploadedFiles: result.response.contractFiles ?? []
                    },
                    customRouteName: result.response.contract.name
                })
            )
        }
    })

    const { execute: navigateToSettlement, status: navigateToSettlementStatus } = useCommand({
        commandFn: async (item: IApprovalRequest) => ApiClient.getSettlementDetails(item.approvalEntityUUID),
        onFinish: result => {
            if (result.status === 'success') {
                dispatch(
                    changeRoute({
                        route: 'settlement-details',
                        params: {
                            settlementId: result.response.settlement.uuid,
                            contractId: result.response.settlement.contractId,
                            eeId: result.response.settlement.executionEnvironmentId
                        }
                    })
                )
            }
        }
    })

    const handleManageApproval = (item: IApprovalRequest) => {
        setSelectedApproval(item)
        setOpenModal(true)
    }

    const handleCloseModal = () => {
        setOpenModal(false)
        setSelectedApproval(null)
    }

    const handleRowClick = async (approval: IApprovalRequest) => {
        switch (approval.entityType) {
            case 'contract':
                navigateToContract(approval)
                break
            case 'schema':
                dispatch(
                    changeRoute({
                        route: 'contract-schema-editor',
                        params: { uuid: approval.approvalEntityUUID }
                    })
                )
                break
            case 'flow':
                dispatch(
                    changeRoute({
                        route: 'flow',
                        params: { flowId: approval.approvalEntityUUID }
                    })
                )
                break
            case 'settlement':
                navigateToSettlement(approval)
                break
            default:
                console.warn(`Unknown entityType: ${approval.entityType}`)
                break
        }
    }

    const handleEditApproval = (approval: IApprovalRequest) => {
        setOpenModal(false)
        setSelectedApproval(null)

        switch (approval.entityType) {
            case 'contract':
                navigateToContract(approval)
                break
            case 'schema':
                dispatch(
                    changeRoute({
                        route: 'contract-schema-editor',
                        params: { uuid: approval.approvalEntityUUID }
                    })
                )
                break
            case 'flow':
                dispatch(
                    changeRoute({
                        route: 'flow',
                        params: { flowId: approval.approvalEntityUUID }
                    })
                )
                break
            case 'settlement':
                navigateToSettlement(approval)
                break
            default:
                console.warn(`Unknown entityType: ${approval.entityType}`)
                break
        }
    }

    const isLoading = navigateToContractStatus === 'loading' || navigateToSettlementStatus === 'loading'

    return (
        <Tile.Container>
            {isLoading && (
                <Box
                    position='fixed'
                    top={0}
                    left={0}
                    width='100%'
                    height='100%'
                    bgcolor='rgba(0, 0, 0, 0.3)'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    zIndex={9999}>
                    <CircularProgress size={60} />
                </Box>
            )}
            <Stack direction='row'>
                <Stack spacing={1}>
                    <Typography variant='h5'>
                        <strong>Approvals Repository</strong>
                    </Typography>
                    <Typography variant='body1'>
                        Manage and track your approvals here
                    </Typography>
                </Stack>
            </Stack>

            <Stack spacing={3}>
                <ApprovalTableUsers onManageApproval={handleManageApproval} onRowClick={handleRowClick} />

                <ApprovalUsersModal
                    open={openModal}
                    onClose={handleCloseModal}
                    approval={selectedApproval}
                    onEdit={handleEditApproval}
                />
            </Stack>
        </Tile.Container>

    )
}

export default ApprovalsUsers
